/* eslint-disable react/no-find-dom-node */
import React from 'react';
import {findDOMNode} from 'react-dom';
import cx from 'classnames';
import contains from './contains';
import injectStyle from './injectStyle';
import PropTypes from 'prop-types';

// Auto inject the styles (will only be done once)
injectStyle();

export default class FlipCard extends React.Component {
  static displayName = 'ReactFlipCard'
  static propTypes = {
    type: PropTypes.string,
    flipped: PropTypes.bool,
    disabled: PropTypes.bool,
    onFlip: PropTypes.func,
    onKeyDown: PropTypes.func,
    children(props, propName, componentName) {
      const prop = props[propName];

      if (React.Children.count(prop) !== 2) {
        return new Error(
          '`' + componentName + '` ' +
          'should contain exactly two children. ' +
          'The first child represents the front of the card. ' +
          'The second child represents the back of the card.'
        );
      }
    }
  }

  static defaultProps = {
    type: 'horizontal',
    flipped: false,
    disabled: false
  }

  constructor(props) {
    super(props);

    this.frontRef = React.createRef();
    this.backRef = React.createRef();

    this.state = {
      hasFocus: false,
      isFlipped: this.props.flipped,
    }
  }

  componentDidMount() {
    this._hideFlippedSide();
  }

  componentWillReceiveProps(newProps) {
    // Make sure both sides are displayed for animation
    this._showBothSides();

    // Wait for display above to take effect
    setTimeout(() => {
      this.setState({
        isFlipped: newProps.flipped
      });
    }, 0);
  }

  componentWillUpdate(nextProps, nextState) {
    // If card is flipping to back via props, track element for focus
    if (!this.props.flipped && nextProps.flipped) {
      // The element that focus will return to when flipped back to front
      this.focusElement = document.activeElement;
      // Indicates that the back of card needs focus
      this.focusBack = true;
    }

    // If isFlipped has changed need to notify
    if (this.state.isFlipped !== nextState.isFlipped) {
      this.notifyFlip = true;
    }
  }

  componentDidUpdate() {
    // If card has flipped to front, and focus is still within the card
    // return focus to the element that triggered flipping to the back.
    if (!this.props.flipped &&
      this.focusElement &&
      contains(findDOMNode(this), document.activeElement)
    ) {
      this.focusElement.focus();
      this.focusElement = null;
    }
      // Direct focus to the back if needed
    /* eslint brace-style:0 */
    else if (this.focusBack) {
      if (this.backRef.focus) this.backRef.focus();
      this.focusBack = false;
    }

    // Notify card being flipped
    if (this.notifyFlip && typeof this.props.onFlip === 'function') {
      this.props.onFlip(this.state.isFlipped);
      this.notifyFlip = false;
    }

    // Hide whichever side of the card is down
    setTimeout(this._hideFlippedSide.bind(this), 600);
  }

  handleFocus() {
    if (this.props.disabled) return;

    this.setState({
      isFlipped: true
    });
  }

  handleBlur() {
    if (this.props.disabled) return;

    this.setState({
      isFlipped: false
    });
  }

  handleKeyDown(e) {
    if (typeof this.props.onKeyDown === 'function') {
      this.props.onKeyDown(e);
    }
  }

  _showBothSides() {
    if (this.frontRef.style) this.frontRef.style.display = '';
    if (this.backRef.style) this.backRef.style.display = '';
  }

  _hideFlippedSide() {
    // This prevents the flipped side from being tabbable
    if (this.props.disabled) {
      if (this.state.isFlipped) {
        if (this.frontRef.style) this.frontRef.style.display = 'none';
      } else {
        if (this.backRef.style) this.backRef.style.display = 'none';
      }
    }
  }

  render() {
    const {children} = this.props;
    if (!children || !children.length) return null;

    return (
      <div
        className={cx({
          'ReactFlipCard': true,
          'ReactFlipCard--vertical': this.props.type === 'vertical',
          'ReactFlipCard--horizontal': this.props.type !== 'vertical',
          'ReactFlipCard--flipped': this.state.isFlipped,
          'ReactFlipCard--enabled': !this.props.disabled
        })}
        tabIndex={0}
        onFocus={this.handleFocus.bind(this)}
        onBlur={this.handleBlur.bind(this)}
        onKeyDown={this.handleKeyDown.bind(this)}
      >
        <div
          className="ReactFlipCard__Flipper w-full h-full"
        >
          <div
            className="ReactFlipCard__Front w-full h-full"
            ref={this.frontRef}
            tabIndex={-1}
            aria-hidden={this.state.isFlipped}
          >
            {this.props.children[0]}
          </div>
          <div
            className="ReactFlipCard__Back w-full h-full"
            ref={this.backRef}
            tabIndex={-1}
            aria-hidden={!this.state.isFlipped}
          >
            {this.props.children[1]}
          </div>
        </div>
      </div>
    );
  }
}
